// src/context/ChatContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

export function ChatProvider({ children }) {
  const { currentUser } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [isOperating, setIsOperating] = useState(false); // For Firestore writes

  // Memoized version of createInitialSession
  const createInitialSession = useCallback(async (userId) => {
    if (!userId) {
      console.warn('Create initial session: No user ID provided.');
      return null;
    }
    setIsOperating(true);
    const newSessionData = {
      userId: userId,
      title: 'New Chat', // Or a more dynamic title if needed
      messages: [],
      createdAt: serverTimestamp(),
      lastUpdatedAt: serverTimestamp(),
    };
    try {
      const docRef = await addDoc(collection(db, 'chatSessions'), newSessionData);
      const newSessionWithIdForState = {
        ...newSessionData,
        id: docRef.id,
        // Use client-side Timestamps for optimistic UI update consistency
        createdAt: Timestamp.now(),
        lastUpdatedAt: Timestamp.now(),
      };
      setSessions([newSessionWithIdForState]); // This is the first and only session
      setActiveId(docRef.id);
      setMessages([]); // Ensure messages are empty for the new session
      console.log('Initial session created and activated:', docRef.id);
      return docRef.id; // Return the new session ID
    } catch (error) {
      console.error('Error creating initial session:', error);
      // Potentially set an error state for the UI here
      return null;
    } finally {
      setIsOperating(false);
    }
  }, []); // No dependencies from ChatProvider scope, as setters are stable

  // Effect for fetching/initializing sessions
  useEffect(() => {
    console.log('ChatContext useEffect triggered:', { 
      hasCurrentUser: !!currentUser,
      currentUserId: currentUser?.uid,
      sessionCount: sessions.length,
      activeId
    });

    if (!currentUser) {
      console.log('No current user, resetting chat state');
      setSessions([]);
      setMessages([]);
      setActiveId(null);
      setIsLoadingSessions(false);
      return;
    }

    setIsLoadingSessions(true);
    console.log('Loading sessions for user:', currentUser.uid);
    
    // Add a safety timeout to force loading state to complete
    // even if Firestore operations fail silently
    const safetyTimeoutId = setTimeout(() => {
      if (isLoadingSessions) {
        console.warn('⚠️ Sessions loading timed out after 8 seconds - applying emergency bypass');
        setIsLoadingSessions(false);
        
        // If we still don't have an active session, create a local-only fallback session
        if (!activeId) {
          console.log('Creating emergency local-only session');
          const emergencySessionId = 'local-' + Date.now();
          const emergencySession = {
            id: emergencySessionId,
            userId: currentUser.uid,
            title: 'New Chat (Local)',
            messages: [],
            createdAt: Timestamp.now(),
            lastUpdatedAt: Timestamp.now(),
            isLocalOnly: true // Flag to indicate this session exists only in memory
          };
          
          setSessions([emergencySession]);
          setActiveId(emergencySessionId);
          setMessages([]);
        }
      }
    }, 8000);
    
    try {
      const sessionsColRef = collection(db, 'chatSessions');
      const q = query(
        sessionsColRef,
        where('userId', '==', currentUser.uid),
        orderBy('lastUpdatedAt', 'desc')
      );

      // Consider using onSnapshot for real-time updates to the sessions list
      // For now, sticking with getDocs as in the original provided code
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
          console.log('Session snapshot received, count:', querySnapshot.size);
          const fetchedSessions = [];
          querySnapshot.forEach((doc) => {
              fetchedSessions.push({ id: doc.id, ...doc.data() });
          });
          setSessions(fetchedSessions);

          if (fetchedSessions.length > 0) {
              console.log('Sessions found:', fetchedSessions.length);
              // If no activeId, or activeId no longer exists, set to the first one
              if (!activeId || !fetchedSessions.some(s => s.id === activeId)) {
                  const newActiveId = fetchedSessions[0].id;
                  console.log('Setting active session to:', newActiveId);
                  setActiveId(newActiveId);
                  // Important: Load messages for the newly set active session
                  const currentActiveSession = fetchedSessions.find(s => s.id === newActiveId);
                  setMessages(currentActiveSession?.messages || []);
              }
          } else if (currentUser && currentUser.uid) { // Check currentUser.uid to be safe
              console.log('No sessions found, creating initial session');
              // No sessions exist, create an initial one
              createInitialSession(currentUser.uid).catch(error => {
                console.error('Failed to create initial session:', error);
                setIsLoadingSessions(false);
              });
          }
          setIsLoadingSessions(false);
      }, (error) => {
          console.error('Error fetching chat sessions with onSnapshot:', error);
          setIsLoadingSessions(false);
          // Optionally try to create an initial session if fetching fails and no sessions are loaded
          if (sessions.length === 0 && currentUser && currentUser.uid) {
               console.log('Error occurred, attempting fallback session creation');
               createInitialSession(currentUser.uid).catch(err => {
                 console.error('Fallback session creation failed:', err);
               });
          }
      });

      // Return cleanup function
      return () => {
        console.log('Unsubscribing from sessions snapshot');
        unsubscribe();
        clearTimeout(safetyTimeoutId);
      };
    } catch (outerError) {
      console.error('Unexpected error in ChatContext useEffect:', outerError);
      setIsLoadingSessions(false);
      clearTimeout(safetyTimeoutId);
      return () => {}; // Empty cleanup function
    }
  }, [currentUser, createInitialSession]);


  // newSession: Called when user explicitly clicks "New Chat"
  const newSession = useCallback(async () => {
    if (!currentUser) {
      console.warn('Cannot create new session: no user.');
      return null; // Indicate failure
    }
    setIsOperating(true);
    const newSessionData = {
      userId: currentUser.uid,
      title: `Chat ${sessions.length + 1}`, // More dynamic title
      messages: [],
      createdAt: serverTimestamp(),
      lastUpdatedAt: serverTimestamp(),
    };
    try {
      const docRef = await addDoc(collection(db, 'chatSessions'), newSessionData);
      // Optimistic update for state (using client-side timestamps for immediate feedback)
      const newSessionForState = {
        ...newSessionData,
        id: docRef.id,
        createdAt: Timestamp.now(),
        lastUpdatedAt: Timestamp.now(),
      };

      // Update sessions list (onSnapshot should ideally pick this up too,
      // but manual update provides quicker UI feedback if onSnapshot has latency)
      setSessions(prevSessions =>
        [newSessionForState, ...prevSessions].sort((a, b) =>
          (b.lastUpdatedAt?.toMillis() || 0) - (a.lastUpdatedAt?.toMillis() || 0)
        )
      );
      setActiveId(docRef.id);
      setMessages([]); // Critical: clear messages for the new chat
      console.log('New session created and activated:', docRef.id);
      return docRef.id; // Return the new session ID
    } catch (error) {
      console.error('Error creating new session in Firestore:', error);
      // TODO: Display this error to the user (e.g., via a toast/snackbar)
      return null; // Indicate failure
    } finally {
      setIsOperating(false);
    }
  }, [currentUser, sessions.length]); // sessions.length for dynamic title, setters are stable

  // deleteSession: Delete a chat session
  const deleteSession = useCallback(async (sessionId) => {
    if (!currentUser || !sessionId) {
      console.warn('Cannot delete session: no user or session ID.');
      return false;
    }

    // Verify that the session belongs to the current user for security
    const targetSession = sessions.find(s => s.id === sessionId);
    if (!targetSession || targetSession.userId !== currentUser.uid) {
      console.warn('Cannot delete session: session not found or not owned by current user.');
      return false;
    }

    setIsOperating(true);
    try {
      // Delete the document from Firestore
      await deleteDoc(doc(db, 'chatSessions', sessionId));

      // Optimistic UI update
      setSessions(prevSessions => prevSessions.filter(s => s.id !== sessionId));

      // If the deleted session was active, switch to another session
      if (sessionId === activeId) {
        const remainingSessions = sessions.filter(s => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          // Switch to the first available session
          setActiveId(remainingSessions[0].id);
          setMessages(remainingSessions[0].messages || []);
        } else {
          // No sessions left, clear active state
          setActiveId(null);
          setMessages([]);
          // Optionally create a new session automatically
          if (currentUser && currentUser.uid) {
            await createInitialSession(currentUser.uid);
          }
        }
      }

      console.log('Session deleted:', sessionId);
      return true;
    } catch (error) {
      console.error('Error deleting session:', error);
      return false;
    } finally {
      setIsOperating(false);
    }
  }, [currentUser, sessions, activeId, createInitialSession]);

  // saveMessage (renamed to addMessage in context value for clarity)
  const saveMessage = useCallback(async (messageObject) => {
    if (!currentUser || !activeId) {
      console.warn('Cannot save message: no user or active session.');
      return; // Or throw an error / return failure
    }
    if (!messageObject || !messageObject.text || !messageObject.sender) {
        console.warn('Cannot save message: invalid message object.', messageObject);
        return;
    }

    setIsOperating(true);
    const newLastUpdatedAt = Timestamp.now(); // For optimistic local update

    // Optimistic UI update for messages list in the active chat
    setMessages((prevMessages) => [...prevMessages, messageObject]);

    // Optimistic UI update for sessions list (update timestamp and sort)
    setSessions(prevSessions =>
      prevSessions.map(session =>
        session.id === activeId
          ? {
            ...session,
            // Ensure messages in the session are also updated optimistically.
            // This avoids stale messages if user switches sessions before Firestore write completes.
            messages: [...(session.messages || []), messageObject],
            lastUpdatedAt: newLastUpdatedAt, // Optimistic timestamp
            // If you store a title snippet from the first message, update it here too.
            // title: session.messages.length === 0 ? messageObject.text.substring(0,30) : session.title,
          }
          : session
      ).sort((a, b) => (b.lastUpdatedAt?.toMillis() || 0) - (a.lastUpdatedAt?.toMillis() || 0))
    );

    try {
      // Check if this is a local-only session
      const activeSession = sessions.find(s => s.id === activeId);
      if (activeSession?.isLocalOnly) {
        console.log('Skipping Firestore update for local-only session');
        setIsOperating(false);
        return;
      }

      const sessionRef = doc(db, 'chatSessions', activeId);
      // Get the most current messages from the state to ensure atomicity
      // This could also be a Firestore arrayUnion operation for messages.
      const sessionToUpdate = sessions.find(s => s.id === activeId);
      const messagesForFirestore = sessionToUpdate
          ? [...(sessionToUpdate.messages || []), messageObject] // Use messages from state that includes new one
          : [messageObject]; // Fallback, ideally sessionToUpdate is always found

      // Update title to first user message if this is the first message
      const shouldUpdateTitle = sessionToUpdate && 
                              sessionToUpdate.messages.length === 0 && 
                              messageObject.sender === 'user';
      
      const newTitle = shouldUpdateTitle
        ? messageObject.text.substring(0, 30) + (messageObject.text.length > 30 ? '...' : '')
        : sessionToUpdate?.title || 'New Chat';

      await updateDoc(sessionRef, {
        messages: messagesForFirestore, // Update with the full array
        lastUpdatedAt: serverTimestamp(), // Server-side timestamp for canonical ordering
        title: newTitle,
      });
      
      // Update title in local state for immediate feedback
      if (shouldUpdateTitle) {
        setSessions(prevSessions =>
          prevSessions.map(session =>
            session.id === activeId
              ? { ...session, title: newTitle }
              : session
          )
        );
      }
    } catch (error) {
      console.error('Error saving message to Firestore:', error);
      // Create a local-only session if Firestore operations fail
      if (!sessions.some(s => s.isLocalOnly)) {
        console.log('Firestore error, converting to local-only session');
        const localSession = {
          id: 'local-' + Date.now(),
          userId: currentUser.uid,
          title: 'Local Chat',
          messages: [...messages, messageObject], // Include all existing messages + new one
          createdAt: Timestamp.now(),
          lastUpdatedAt: Timestamp.now(),
          isLocalOnly: true
        };
        
        setSessions(prev => [localSession, ...prev]);
        setActiveId(localSession.id);
      }
    } finally {
      setIsOperating(false);
    }
  }, [currentUser, activeId, sessions, messages]);

  const switchSession = useCallback((targetId) => {
    if (targetId === activeId || !currentUser) {
      return;
    }
    const targetSession = sessions.find(s => s.id === targetId);
    if (targetSession) {
      setActiveId(targetId);
      setMessages(targetSession.messages || []);
      console.log('Switched to session:', targetId);
    } else {
      console.warn(`Switch failed: Session with id ${targetId} not found locally.`);
      // Potentially try to fetch this specific session if not found, or reset
    }
  }, [currentUser, activeId, sessions]); // sessions is a dependency

  // Return as a memoized value object for context consumers
  const value = useMemo(() => ({
    sessions,
    activeId,
    messages,
    isLoadingSessions,
    isOperating,
    newSession,
    deleteSession,
    switchSession,
    addMessage: saveMessage, // Rename for clearer API
    // Expose these setters for emergency override only
    setSessions,
    setActiveId,
    setMessages,
    setIsLoadingSessions
  }), [
    sessions,
    activeId,
    messages,
    isLoadingSessions,
    isOperating,
    newSession,
    deleteSession,
    switchSession,
    saveMessage
  ]);

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};